import { useRef, useState } from "react";
import { useApiFetch } from "../../../api/client";
import { Btn } from "../../../components/ui/Button";
import { Chip } from "../../../components/ui/Chip";
import { errorMessage } from "../../cockpit/format";
import { firstPagesText } from "./tdsPdfText";
import {
  checkWithPdf,
  matchTdsFile,
  uniqueGrades,
  type MatchableGrade,
  type PdfCheck,
  type TdsMatch,
} from "./tdsMatching";
import type { GradeLinkView, GradeTdsAttachResult } from "../types";
import { UploadDropzone } from "../ui/UploadDropzone";
import { TdsMatchingGuide } from "../ui/UploadGuides";
import { UploadStatusBar, type StatusTone } from "../ui/UploadStatusBar";

const FILES_PER_REQUEST = 5;
const PRESELECTED_STATUSES = new Set(["exact", "nameInFile", "fileNameInGrade", "close"]);

type AttachState = "waiting" | "attached" | "alreadyAttached" | "failed";

interface TdsRow {
  rowId: number;
  file: File;
  match: TdsMatch;
  isDuplicate: boolean;
  chosenGradeId: number | null;
  pickedByUser: boolean;
  pdfCheck: PdfCheck | null;
  gradesInPdf: MatchableGrade[];
  attachState: AttachState;
  failureText: string | null;
}

type ChipVariant = "default" | "ok" | "bad" | "warn";

const MATCH_CHIPS: Record<TdsMatch["status"], [ChipVariant, string]> = {
  exact: ["ok", "✓ Exact"],
  nameInFile: ["ok", "✓ Name in file"],
  fileNameInGrade: ["ok", "✓ Name match"],
  close: ["warn", "Close, check"],
  pickOne: ["warn", "Pick one"],
  noMatch: ["default", "No match"],
  notPdf: ["default", "Not a PDF"],
};

const PDF_CHECK_CHIPS: Record<PdfCheck, [ChipVariant, string]> = {
  confirmed: ["ok", "✓ Confirmed by PDF"],
  foundInPdf: ["ok", "✓ Found in PDF"],
  otherGradeInPdf: ["warn", "PDF names another grade"],
  notInPdf: ["warn", "Not in PDF, check"],
};

function rowChip(row: TdsRow): [ChipVariant, string] {
  if (row.attachState === "attached") return ["ok", "Attached"];
  if (row.attachState === "alreadyAttached") return ["ok", "Already attached"];
  if (row.attachState === "failed") return ["bad", "Failed"];
  if (row.isDuplicate) return ["warn", "Same file twice"];
  if (row.pickedByUser) return row.chosenGradeId ? ["ok", "Picked by you"] : ["default", "Skipped"];
  if (row.pdfCheck) return PDF_CHECK_CHIPS[row.pdfCheck];
  if (!row.chosenGradeId && PRESELECTED_STATUSES.has(row.match.status)) return ["default", "Skipped"];
  return MATCH_CHIPS[row.match.status];
}

function isSameFile(first: File, second: File): boolean {
  return first.name === second.name && first.size === second.size;
}

interface Props {
  onAttached: () => void;
}

export function TdsBulkUpload({ onAttached }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const nextRowIdRef = useRef(1);
  const [grades, setGrades] = useState<MatchableGrade[]>([]);
  const [rows, setRows] = useState<TdsRow[]>([]);
  const [sourceLabel, setSourceLabel] = useState("");
  const [revisionMonth, setRevisionMonth] = useState("");
  const [isAttaching, setIsAttaching] = useState(false);
  const [isReadingPdfs, setIsReadingPdfs] = useState(false);
  const [status, setStatus] = useState<{ text: string; tone: StatusTone } | null>(null);

  const readPdfs = async (rowsToRead: TdsRow[], loadedGrades: MatchableGrade[]) => {
    setIsReadingPdfs(true);
    for (const [position, row] of rowsToRead.entries()) {
      setStatus({ text: `Reading PDFs ${position + 1} of ${rowsToRead.length}…`, tone: "info" });
      const pageText = await firstPagesText(row.file).catch(() => "");
      const result = checkWithPdf(row.match, row.chosenGradeId, pageText, loadedGrades);
      setRows((currentRows) =>
        currentRows.map((currentRow) =>
          currentRow.rowId === row.rowId && !currentRow.pickedByUser
            ? { ...currentRow, chosenGradeId: result.gradeId, pdfCheck: result.pdfCheck, gradesInPdf: result.gradesInPdf }
            : currentRow,
        ),
      );
    }
    setIsReadingPdfs(false);
    setStatus(null);
  };

  const addFiles = async (fileList: FileList) => {
    const addedFiles = [...fileList];
    setStatus({ text: "Matching file names to grades…", tone: "info" });
    try {
      const response = await apiFetchRef.current("nomenclature/grades");
      const loadedGrades = uniqueGrades((await response.json()) as GradeLinkView[]);
      setGrades(loadedGrades);
      const newRows: TdsRow[] = [];
      for (const file of addedFiles) {
        const isDuplicate = [...rows, ...newRows].some((row) => isSameFile(row.file, file));
        const match = matchTdsFile(file.name, loadedGrades);
        const preselectedGrade = PRESELECTED_STATUSES.has(match.status) ? match.candidates[0] : undefined;
        newRows.push({
          rowId: nextRowIdRef.current++,
          file,
          match,
          isDuplicate,
          chosenGradeId: isDuplicate ? null : (preselectedGrade?.gradeId ?? null),
          pickedByUser: false,
          pdfCheck: null,
          gradesInPdf: [],
          attachState: "waiting",
          failureText: null,
        });
      }
      setRows((currentRows) => [...currentRows, ...newRows]);
      setStatus(null);
      await readPdfs(
        newRows.filter((row) => !row.isDuplicate && row.match.status !== "notPdf"),
        loadedGrades,
      );
    } catch (error) {
      setStatus({ text: `Couldn't load the grades: ${errorMessage(error)}`, tone: "bad" });
    }
  };

  const chooseGrade = (rowId: number, gradeIdText: string) =>
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.rowId === rowId
          ? { ...row, chosenGradeId: gradeIdText ? Number(gradeIdText) : null, pickedByUser: true }
          : row,
      ),
    );

  const updateRows = (rowIds: number[], changes: Partial<TdsRow>) =>
    setRows((currentRows) => currentRows.map((row) => (rowIds.includes(row.rowId) ? { ...row, ...changes } : row)));

  const rowsToSend = rows.filter(
    (row) =>
      row.chosenGradeId !== null &&
      !row.isDuplicate &&
      row.match.status !== "notPdf" &&
      (row.attachState === "waiting" || row.attachState === "failed"),
  );
  const failedRows = rows.filter((row) => row.attachState === "failed");
  const hasAttachedRows = rows.some((row) => row.attachState === "attached" || row.attachState === "alreadyAttached");

  const attachRows = async (selectedRows: TdsRow[]) => {
    if (!selectedRows.length || isAttaching) return;
    setIsAttaching(true);
    let attachedCount = 0;
    let alreadyAttachedCount = 0;
    let failedCount = 0;
    for (let startIndex = 0; startIndex < selectedRows.length; startIndex += FILES_PER_REQUEST) {
      const requestRows = selectedRows.slice(startIndex, startIndex + FILES_PER_REQUEST);
      setStatus({
        text: `Attaching ${startIndex + 1}–${startIndex + requestRows.length} of ${selectedRows.length}…`,
        tone: "info",
      });
      const formData = new FormData();
      requestRows.forEach((row) => formData.append("files", row.file));
      formData.append(
        "links",
        JSON.stringify(
          requestRows.map((row, fileIndex) => ({
            fileIndex,
            gradeId: row.chosenGradeId,
            sourceLabel: sourceLabel.trim() || null,
            revisionMonth: revisionMonth ? `${revisionMonth}-01` : null,
          })),
        ),
      );
      try {
        const response = await apiFetchRef.current("nomenclature/grade-tds", { method: "POST", body: formData });
        const results: GradeTdsAttachResult[] = await response.json();
        for (const [fileIndex, row] of requestRows.entries()) {
          const isAlreadyAttached =
            results.find((attachResult) => attachResult.fileIndex === fileIndex)?.status === "AlreadyAttached";
          if (isAlreadyAttached) alreadyAttachedCount++;
          else attachedCount++;
          updateRows([row.rowId], { attachState: isAlreadyAttached ? "alreadyAttached" : "attached", failureText: null });
        }
      } catch (error) {
        failedCount += requestRows.length;
        updateRows(
          requestRows.map((row) => row.rowId),
          { attachState: "failed", failureText: errorMessage(error) },
        );
      }
    }
    const summaryParts = [`${attachedCount} attached.`];
    if (alreadyAttachedCount) summaryParts.push(`${alreadyAttachedCount} were already on their grade.`);
    if (failedCount) summaryParts.push(`${failedCount} failed. Use Retry Failed to send them again.`);
    setStatus({ text: summaryParts.join(" "), tone: failedCount ? "bad" : "ok" });
    setIsAttaching(false);
    if (attachedCount || alreadyAttachedCount) onAttached();
  };

  const clearRows = () => {
    setRows([]);
    setStatus(null);
  };

  const headerCellClasses = "text-left font-normal text-rs-light-gray text-xs px-3 py-2 border-b border-rs-jet";
  const cellClasses = "px-3 py-2 border-b border-rs-jet align-top";

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-4 @min-[720px]:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <UploadDropzone
          accept=".pdf"
          multiple
          fileTypeLabel="PDF"
          title="Drop TDS PDFs here"
          subtitle="or choose them from your computer"
          buttonLabel="Choose Files"
          onFiles={addFiles}
        />
        <TdsMatchingGuide />
      </div>

      {rows.length > 0 && (
        <>
          <div className="flex gap-3 flex-wrap items-end">
            <label className="flex flex-col gap-1 text-xs text-rs-light-gray">
              Source (optional)
              <input
                className="ctl text-[13px] py-1.5"
                maxLength={100}
                placeholder="e.g. Supplier website"
                value={sourceLabel}
                onChange={(event) => setSourceLabel(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-rs-light-gray">
              Revision month (optional)
              <input
                className="ctl text-[13px] py-1.5"
                type="month"
                value={revisionMonth}
                onChange={(event) => setRevisionMonth(event.target.value)}
              />
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th className={headerCellClasses}>File</th>
                  <th className={headerCellClasses}>Grade</th>
                  <th className={headerCellClasses}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const [chipVariant, chipLabel] = rowChip(row);
                  const suggestedGrades = [
                    ...row.match.candidates,
                    ...row.gradesInPdf.filter(
                      (pdfGrade) => !row.match.candidates.some((grade) => grade.gradeId === pdfGrade.gradeId),
                    ),
                  ];
                  const suggestedIds = new Set(suggestedGrades.map((grade) => grade.gradeId));
                  const isLocked =
                    isAttaching ||
                    row.isDuplicate ||
                    row.match.status === "notPdf" ||
                    row.attachState === "attached" ||
                    row.attachState === "alreadyAttached";
                  return (
                    <tr key={row.rowId}>
                      <td className={`${cellClasses} text-rs-off-white break-all`}>{row.file.name}</td>
                      <td className={cellClasses}>
                        <select
                          className="ctl text-[13px] py-1.5 max-w-[320px]"
                          aria-label={`Grade for ${row.file.name}`}
                          value={row.chosenGradeId ?? ""}
                          disabled={isLocked}
                          onChange={(event) => chooseGrade(row.rowId, event.target.value)}
                        >
                          <option value="">
                            {row.match.status === "pickOne" && !row.pickedByUser ? "Pick a grade…" : "Skip this file"}
                          </option>
                          {suggestedGrades.length > 0 && (
                            <optgroup label="Suggested">
                              {suggestedGrades.map((grade) => (
                                <option key={grade.gradeId} value={grade.gradeId}>
                                  {grade.tradename} · {grade.supplier}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="All saved grades">
                            {grades
                              .filter((grade) => !suggestedIds.has(grade.gradeId))
                              .map((grade) => (
                                <option key={grade.gradeId} value={grade.gradeId}>
                                  {grade.tradename} · {grade.supplier}
                                </option>
                              ))}
                          </optgroup>
                        </select>
                      </td>
                      <td className={cellClasses}>
                        <Chip variant={chipVariant}>{chipLabel}</Chip>
                        {row.failureText && <p className="m-0 mt-1 text-xs text-rs-light-gray">{row.failureText}</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3 flex-wrap items-center">
            <Btn disabled={isAttaching || isReadingPdfs || !rowsToSend.length} onClick={() => attachRows(rowsToSend)}>
              Attach {rowsToSend.length} {rowsToSend.length === 1 ? "File" : "Files"}
            </Btn>
            {failedRows.length > 0 && (
              <Btn variant="ghost" disabled={isAttaching} onClick={() => attachRows(failedRows)}>
                Retry Failed
              </Btn>
            )}
            <Btn variant="ghost" disabled={isAttaching} onClick={clearRows}>
              {hasAttachedRows ? "Done" : "Clear"}
            </Btn>
          </div>
        </>
      )}

      {status && <UploadStatusBar tone={status.tone}>{status.text}</UploadStatusBar>}
    </div>
  );
}
